import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Edit, Upload } from "lucide-react";

export default function AdminCards() {
  const { deckId } = useParams();
  const [deck, setDeck] = useState<any>(null);
  const [list, setList] = useState<any[]>([]);
  const [editing, setEditing] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");

  async function load() {
    const { data: d } = await supabase.from("flashcard_decks").select("*").eq("id", deckId).maybeSingle();
    setDeck(d);
    const { data } = await supabase.from("flashcards").select("*").eq("deck_id", deckId).order("sort_order");
    setList(data || []);
  }
  useEffect(() => { if (deckId) load(); }, [deckId]);

  function newItem() {
    setEditing({ deck_id: deckId, front: "", reading: "", meaning: "", example_sentence: "", example_translation: "", sort_order: list.length });
    setOpen(true);
  }

  async function save() {
    const { id, ...rest } = editing;
    const op = id ? supabase.from("flashcards").update(rest).eq("id", id) : supabase.from("flashcards").insert(rest);
    const { error } = await op;
    if (error) toast.error(error.message);
    else { toast.success("Saved"); setOpen(false); load(); }
  }

  async function bulkAdd() {
    const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    const rows = lines.map((l, i) => {
      const [front, reading, meaning, example_sentence, example_translation] = l.split(",").map((x) => x.trim());
      return { deck_id: deckId, front, reading, meaning, example_sentence, example_translation, sort_order: list.length + i };
    }).filter((r) => r.front && r.meaning);
    if (!rows.length) { toast.error("No valid rows"); return; }
    const { error } = await supabase.from("flashcards").insert(rows);
    if (error) toast.error(error.message);
    else { toast.success(`Added ${rows.length} cards`); setBulkOpen(false); setBulkText(""); load(); }
  }

  async function del(id: string) {
    if (!confirm("Delete card?")) return;
    await supabase.from("flashcards").delete().eq("id", id);
    load();
  }

  return (
    <div className="space-y-4">
      <Link to="/admin/decks" className="text-sm hover:underline">← Decks</Link>
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Cards - {deck?.title}</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBulkOpen(true)}><Upload className="h-4 w-4 mr-1" />Bulk add</Button>
          <Button onClick={newItem}>Add card</Button>
        </div>
      </div>
      <div className="grid gap-2">
        {list.map((c) => (
          <Card key={c.id}>
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex gap-4 items-center">
                <div className="text-2xl font-bold w-24">{c.front}</div>
                <div className="text-sm text-muted-foreground">{c.reading}</div>
                <div className="text-sm">{c.meaning}</div>
              </div>
              <div className="flex gap-2">
                <Button size="icon" variant="ghost" onClick={() => { setEditing(c); setOpen(true); }}><Edit className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(c.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} card</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Front (kanji/word)</Label><Input value={editing.front} onChange={(e) => setEditing({ ...editing, front: e.target.value })} /></div>
              <div><Label>Reading (kana)</Label><Input value={editing.reading || ""} onChange={(e) => setEditing({ ...editing, reading: e.target.value })} /></div>
              <div><Label>Meaning</Label><Input value={editing.meaning} onChange={(e) => setEditing({ ...editing, meaning: e.target.value })} /></div>
              <div><Label>Example sentence</Label><Input value={editing.example_sentence || ""} onChange={(e) => setEditing({ ...editing, example_sentence: e.target.value })} /></div>
              <div><Label>Example translation</Label><Input value={editing.example_translation || ""} onChange={(e) => setEditing({ ...editing, example_translation: e.target.value })} /></div>
              <Button onClick={save} className="w-full">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bulk add cards</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">One card per line: <code>front,reading,meaning,example,translation</code></p>
            <Textarea rows={12} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder="水,みず,water,水をください。,Water please." />
            <Button onClick={bulkAdd} className="w-full">Add all</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
